import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, 
  TableContainer, Tab, Tabs, Card, CardContent, Grid, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  InputAdornment, MenuItem
} from '@mui/material';
import { Settings, Edit, Delete, Warning, Euro, CalendarMonth, Speed, Layers, Add, Autorenew } from '@mui/icons-material';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import Loader from 'UI/Application/Components/Loaders/Loader';
import getAPIResponse from "Shared/Utils/Domain/Services/GetAPIResponsePrivateService";

function InstallationExtraListView() {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [priceForm, setPriceForm] = useState({ startDate: '', price: '' });
  const [savingPrice, setSavingPrice] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    phase: '',
    category: '',
    weightRange: '',
    elevatorStop: '',
    business: '',
    measuringRange: '',
    name: '',
    theoreticalQuantity: '',
    startDate: '',
  });
  const [savingRecord, setSavingRecord] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const [updateWeightsDialogOpen, setUpdateWeightsDialogOpen] = useState(false);
  const [updateWeightsPhase, setUpdateWeightsPhase] = useState('');
  const [updateWeightsForm, setUpdateWeightsForm] = useState({});
  const [savingWeights, setSavingWeights] = useState(false);
  const [searchQueries, setSearchQueries] = useState({ 0: '', 1: '', 2: '' });

  const zpmcFields = [
    'Reparto de material',
    'Montaje de Guías',
    'Autocontrol',
    'Replanteo',
    'Arranque de Guías',
    'Puesta en Tiro',
    'Montaje de Puertas'
  ];

  const zpelFields = [
    'Entrega a Cliente y PV',
    'Montaje Armario Maniobra',
    'Montaje de Cabina',
    'Autocontrol',
    'Ajustes, Regulación y Autocontrol',
    'Instalación de Hueco'
  ];

  const openUpdateWeightsDialog = (phase) => {
    const fields = phase === 'ZPEL' ? zpelFields : zpmcFields;
    const initialForm = {};
    fields.forEach(field => {
      const existing = data.find(item => item.phase === phase && item.name === field);
      initialForm[field] = existing ? String(existing.theoreticalQuantity ?? '') : '';
    });
    setUpdateWeightsPhase(phase);
    setUpdateWeightsForm(initialForm);
    setUpdateWeightsDialogOpen(true);
  };

  const closeUpdateWeightsDialog = () => {
    setUpdateWeightsDialogOpen(false);
    setUpdateWeightsPhase('');
    setUpdateWeightsForm({});
  };

  const handleUpdateWeights = async () => {
    setSavingWeights(true);
    const fields = updateWeightsPhase === 'ZPEL' ? zpelFields : zpmcFields;
    const promises = [];

    try {
      for (const field of fields) {
        const newValue = updateWeightsForm[field];
        if (newValue === '') continue;

        const existing = data.find(item => item.phase === updateWeightsPhase && item.name === field);
        const quantity = Number(newValue);

        if (existing) {
          if (existing.theoreticalQuantity !== quantity) {
            const payload = {
              id: existing.id,
              phase: updateWeightsPhase,
              name: field,
              theoreticalQuantity: quantity,
              ot: existing.ot || '123456',
              endDate: existing.endDate || '9999-12-31 23:59:59',
              equipmentId: existing.equipmentId ?? null,
              finalQuantity: existing.finalQuantity ?? 0.0,
              loadingOrigin: existing.loadingOrigin ?? '',
              paymentDate: existing.paymentDate ?? null,
              delegationId: existing.delegationId ?? null,
            };
            promises.push(getAPIResponse(`/v1/installation-variable-assign-weights/${existing.id}`, payload, 'PUT'));
          }
        } else {
          const payload = {
            phase: updateWeightsPhase,
            name: field,
            theoreticalQuantity: quantity,
            ot: '123456',
            endDate: '9999-12-31 23:59:59',
            equipmentId: null,
            finalQuantity: 0.0,
            loadingOrigin: '',
            paymentDate: null,
          };
          promises.push(getAPIResponse('/v1/installation-variable-assign-weights', payload, 'POST'));
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        toast.success('Pesos actualizados correctamente');
        const refreshed = await getAPIResponse('/v1/installation-variable-assign-weights', { offset: 0, limit: 100 }, 'GET');
        setData(refreshed?.items || refreshed?.records || refreshed || []);
      }
      closeUpdateWeightsDialog();
    } catch (error) {
      console.error("Error al actualizar pesos:", error);
      toast.error("Error al actualizar algunos pesos");
    } finally {
      setSavingWeights(false);
    }
  };

  const elevatorTypeOptions = ['MECÁNICA ASCENSOR', 'ELÉCTRICA ASCENSOR'];
  const categoryOptions = ['ELECTRO', 'OLEO', 'OLEO/HOME', 'GRL/ELEC'];
  const weightRangeOptions = [
    { label: '0 - 480kg', value: '0-480', startWeight: 0, endWeight: 480 },
    { label: '481kg - 750kg', value: '481-750', startWeight: 481, endWeight: 750 },
    { label: '751kg - 1000kg', value: '751-1000', startWeight: 751, endWeight: 1000 },
  ];
  const stopOptions = Array.from({ length: 14 }, (_, index) => String(index + 2));
  const extraCategoryOptions = [
    'Maniobra adicional en hueco',
    'Guias de 2,5 mtos. excepto en Homelift',
    'Ascensores en comunidades',
    'Doble acceso 90º GRL y Oleo',
    'Doble acceso simultáneos',
    'Descarga y acondicionamiento de materiales',
    'Montajes fuera de zona más de 30 km',
    'Montajes fuera de zona más pernoctar',
    'Doble acceso 90º, 180º',
    'Puesta en marcha hasta 4 paradas',
    'Parada de más de 4',
    'Puerta semi automática',
    'Puerta automática',
  ];
  const businessOptions = [
    'General',
    'Negocio 60',
    'Negocio 70',
    'Contrato',
    'Toma de datos',
  ];
  const measuringRangeOptions = [
    'Por ascensor',
    'Por parada',
    'Por puerta',
    'Por precio total',
  ];

  const loadTabData = useCallback(async (tabIndex) => {
    setLoading(true);
    setData([]);
    let endpoint = '';
    
    if (tabIndex === 0) {
      endpoint = '/v1/installation-variable-elevators';
    } else if (tabIndex === 1) {
      endpoint = '/v1/installation-variable-extra-elevators';
    } else if (tabIndex === 2) {
      endpoint = '/v1/installation-variable-assign-weights';
    }

    try {
      const response = await getAPIResponse(endpoint, { offset: 0, limit: 100 }, "GET");
      setData(response?.items || response?.records || response || []);
    } catch (error) {
      console.error("Error al cargar datos de instalación extra:", error);
      toast.error("Error al cargar la información de instalación extra");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTabData(tabValue);
  }, [tabValue, loadTabData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getSearchableText = (value) => String(value ?? '').toLowerCase();
  const searchQuery = searchQueries[tabValue] ?? '';

  const filteredData = data.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return true;
    }

    if (tabValue === 0) {
      return [
        item.phase,
        item.category,
        item.startWeight ?? item.start_weight,
        item.endWeight ?? item.end_weight,
        item.elevatorStop ?? item.elevator_stop,
      ].some((field) => getSearchableText(field).includes(query));
    }

    if (tabValue === 1) {
      return [
        item.phase,
        item.category,
        item.business,
        item.measuringRange ?? item.measuring_range,
      ].some((field) => getSearchableText(field).includes(query));
    }

    return [
      item.phase,
      item.name,
      item.theoreticalQuantity,
    ].some((field) => getSearchableText(field).includes(query));
  });

  const getPriceEndpointBase = useCallback((item = selectedItem) => {
    if (!item) {
      return '';
    }

    if (tabValue === 0) {
      return `/v1/installation-variable-elevators/${item.id}/prices`;
    }

    if (tabValue === 1) {
      return `/v1/installation-variable-extra-elevators/${item.id}/prices`;
    }

    return '';
  }, [selectedItem, tabValue]);

  const getRecordEndpoint = useCallback((item = selectedItem) => {
    if (!item) {
      return '';
    }

    if (tabValue === 0) {
      return `/v1/installation-variable-elevators/${item.id}`;
    }

    if (tabValue === 1) {
      return `/v1/installation-variable-extra-elevators/${item.id}`;
    }

    if (tabValue === 2) {
      return `/v1/installation-variable-assign-weights/${item.id}`;
    }

    return '';
  }, [selectedItem, tabValue]);

  const openPriceDialog = (item) => {
    setSelectedItem(item);
    setPriceForm({ startDate: '', price: '' });
    setPriceDialogOpen(true);
  };

  const closePriceDialog = () => {
    setPriceDialogOpen(false);
    setSelectedItem(null);
    setPriceForm({ startDate: '', price: '' });
  };

  const getWeightRangeValue = (item) => {
    const startWeight = item.startWeight ?? item.start_weight ?? 0;
    const endWeight = item.endWeight ?? item.end_weight ?? 0;
    const matchingRange = weightRangeOptions.find((range) => (
      range.startWeight === Number(startWeight) && range.endWeight === Number(endWeight)
    ));

    return matchingRange?.value || '';
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setEditForm({
      phase: item.phase || '',
      category: item.category || '',
      weightRange: getWeightRangeValue(item),
      elevatorStop: String(item.elevatorStop || item.elevator_stop || item.extraStop || item.extra_stop || ''),
      business: item.business || '',
      measuringRange: item.measuringRange || item.measuring_range || '',
      name: item.name || '',
      theoreticalQuantity: String(item.theoreticalQuantity ?? ''),
    });
    setIsCreateMode(false);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedItem(null);
    setEditForm({
      phase: '',
      category: '',
      weightRange: '',
      elevatorStop: '',
      business: '',
      measuringRange: '',
      name: '',
      theoreticalQuantity: '',
      startDate: '',
    });
    setIsCreateMode(false);
  };

  const openCreateDialog = () => {
    setSelectedItem(null);
    setEditForm({
      phase: '',
      category: '',
      weightRange: '',
      elevatorStop: '',
      business: '',
      measuringRange: '',
      name: '',
      theoreticalQuantity: '',
      startDate: '',
    });
    setIsCreateMode(true);
    setEditDialogOpen(true);
  };

  const handleCreateRecord = async () => {
    if ((tabValue === 0 || tabValue === 1) && (!editForm.phase || !editForm.category)) {
      toast.error('Indica el tipo de ascensor y la categoría');
      return;
    }

    let payload = {};
    if (tabValue === 0 || tabValue === 1) {
      payload = {
        phase: editForm.phase,
        category: editForm.category,
      };
    }

    if (tabValue === 0) {
      const selectedWeightRange = weightRangeOptions.find((range) => range.value === editForm.weightRange);
      const elevatorStop = Number(editForm.elevatorStop);

      if (!selectedWeightRange || Number.isNaN(elevatorStop)) {
        toast.error('Indica el peso y la parada');
        return;
      }

      payload = {
        ...payload,
        startWeight: selectedWeightRange.startWeight,
        endWeight: selectedWeightRange.endWeight,
        elevatorStop,
      };
    }

    if (tabValue === 1) {
      payload = {
        ...payload,
        business: editForm.business,
        measuringRange: editForm.measuringRange,
      };
    }

    if (tabValue === 2) {
      if (!editForm.phase || !editForm.name || editForm.theoreticalQuantity === '') {
        toast.error('Indica la fase, el tipo y el peso');
        return;
      }

      payload = {
        phase: editForm.phase,
        name: editForm.name,
        theoreticalQuantity: Number(editForm.theoreticalQuantity),
        startDate: editForm.startDate || new Date().toISOString().slice(0, 10),
      };
    }

    setSavingRecord(true);
    try {
      // Add required fields the backend expects
      const fullPayload = {
        ot: '123456',
        endDate: '9999-12-31 23:59:59',
        equipmentId: null,
        theoreticalQuantity: 0.0,
        finalQuantity: 0.0,
        loadingOrigin: '',
        paymentDate: null,
        ...payload,
      };

      if (tabValue === 2) {
        fullPayload.startDate = payload.startDate;
      }

      const endpoint = tabValue === 0 ? '/v1/installation-variable-elevators' : (tabValue === 1 ? '/v1/installation-variable-extra-elevators' : '/v1/installation-variable-assign-weights');
      const response = await getAPIResponse(endpoint, fullPayload, 'POST');
      if (response) {
        setData((currentData) => [response, ...currentData]);
        toast.success('Registro creado correctamente');
        closeEditDialog();
      }
    } finally {
      setSavingRecord(false);
    }
  };

  const formatDate = (date) => {
    if (!date) {
      return '-';
    }

    if (date.startsWith('9999-12-31')) {
      return '-';
    }

    const [year, month, day] = date.split(' ')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const formatPrice = (price) => {
    const numericPrice = Number(price || 0);
    return `${numericPrice.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  };

  const handleCreatePrice = async () => {
    if (!selectedItem || !priceForm.startDate || priceForm.price === '') {
      toast.error('Indica la fecha de inicio y el precio');
      return;
    }

    const parsedPrice = Number(String(priceForm.price).replace(',', '.'));
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      toast.error('El precio debe ser un número válido');
      return;
    }

    setSavingPrice(true);
    try {
      const response = await getAPIResponse(getPriceEndpointBase(), {
        startDate: `${priceForm.startDate} 00:00:00`,
        endDate: '9999-12-31 23:59:59',
        price: parsedPrice,
      }, 'POST');

      if (response) {
        setData((currentData) => currentData.map((item) => (
          item.id === selectedItem.id
            ? { ...item, prices: [...(item.prices || []), response] }
            : item
        )));
        setSelectedItem((currentItem) => ({
          ...currentItem,
          prices: [...(currentItem?.prices || []), response],
        }));
        setPriceForm({ startDate: '', price: '' });
        toast.success('Precio guardado correctamente');
      }
    } finally {
      setSavingPrice(false);
    }
  };


  const handleUpdateRecord = async () => {
    if (!selectedItem) {
      return;
    }

    if (tabValue === 0 || tabValue === 1) {
      if (!editForm.phase || !editForm.category) {
        toast.error('Indica el tipo de ascensor y la categoría');
        return;
      }
    }

    let payload = {};
    if (tabValue === 0 || tabValue === 1) {
      payload = {
        id: selectedItem.id,
        phase: editForm.phase,
        category: editForm.category,
      };
    }

    if (tabValue === 0) {
      const selectedWeightRange = weightRangeOptions.find((range) => range.value === editForm.weightRange);
      const elevatorStop = Number(editForm.elevatorStop);

      if (!selectedWeightRange || Number.isNaN(elevatorStop)) {
        toast.error('Indica el peso y la parada');
        return;
      }

      payload = {
        ...payload,
        startWeight: selectedWeightRange.startWeight,
        endWeight: selectedWeightRange.endWeight,
        elevatorStop,
      };
    }

    if (tabValue === 1) {
      payload = {
        ...payload,
        business: editForm.business,
        measuringRange: editForm.measuringRange,
      };
    }

    if (tabValue === 2) {
      if (!editForm.phase || !editForm.name || editForm.theoreticalQuantity === '') {
        toast.error('Indica la fase, el tipo y el peso');
        return;
      }

      payload = {
        id: selectedItem.id,
        phase: editForm.phase,
        name: editForm.name,
        theoreticalQuantity: Number(editForm.theoreticalQuantity),
        ot: selectedItem.ot || '123456',
        endDate: selectedItem.endDate || '9999-12-31 23:59:59',
        equipmentId: selectedItem.equipmentId ?? null,
        finalQuantity: selectedItem.finalQuantity ?? 0.0,
        loadingOrigin: selectedItem.loadingOrigin ?? '',
        paymentDate: selectedItem.paymentDate ?? null,
        delegationId: selectedItem.delegationId ?? null,
      };
    }

    setSavingRecord(true);
    try {
      const response = await getAPIResponse(getRecordEndpoint(), payload, 'PUT');
      if (response) {
        const updatedItem = {
          ...selectedItem,
          ...response,
          prices: response.prices || selectedItem.prices || [],
        };

        setData((currentData) => currentData.map((item) => (
          item.id === selectedItem.id ? updatedItem : item
        )));
        toast.success('Registro actualizado correctamente');
        closeEditDialog();
      }
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDeleteRecord = async (item) => {
    if (!item) {
      return;
    }

    if (!window.confirm('¿Está seguro de que desea eliminar este registro?')) {
      return;
    }

    const endpoint = getRecordEndpoint(item);
    if (!endpoint) {
      return;
    }

    try {
      const response = await getAPIResponse(endpoint, {}, 'DELETE');
      if (response !== null) {
        setData((currentData) => currentData.filter((i) => i.id !== item.id));
        toast.success('Registro eliminado correctamente');
      }
    } catch (error) {
      console.error("Error al eliminar registro:", error);
      toast.error("Error al eliminar el registro");
    }
  };

  // Gradient definitions for UI cards
  // Use app primary color (#0492D4) and a darker variant for the corporate gradient
  const corporateBlue = "linear-gradient(135deg, #0492D4 0%, #0066AA 100%)";
  const cardGradient1 = corporateBlue;

  return (
    <Box sx={{ p: 4, minHeight: '85vh', backgroundColor: '#f9fbfd' }}>
      {/* Header (match style of Extra Configuración) */}
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings color="primary" />
            {t('ui.menus.user.installationExtra', 'Instalación Extra')}
          </Typography>
        </Box>
      </Box>

      {/* Tabs Menu */}
      <Paper sx={{ borderRadius: 3, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          indicatorColor="primary" 
          textColor="primary" 
          variant="fullWidth"
          sx={{
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': { py: 2.5, fontWeight: 'bold', fontSize: '0.95rem' }
          }}
        >
          <Tab icon={<Layers sx={{ mr: 1 }} />} iconPosition="start" label="Variables Instalaciones"  />
          <Tab icon={<Speed sx={{ mr: 1 }} />} iconPosition="start" label="Variables Extras Instalaciones" />
          <Tab icon={<CalendarMonth sx={{ mr: 1 }} />} iconPosition="start" label="Asignación de Pesos" />
        </Tabs>
      </Paper>

      {/* Main Content Area */}
      <Box>
        {loading ? (
          <Paper sx={{ py: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Loader />
          </Paper>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }} alignItems="center">
              <Grid item xs={12} md={4}>
                <Card sx={{ background: cardGradient1, color: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(255,107,107,0.25)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="overline" sx={{ fontWeight: 'bold', opacity: 0.8 }}>Registros visibles</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>{filteredData.length}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
                {tabValue === 2 && (
                  <>
                    <Button variant="contained" color="primary" startIcon={<Autorenew />} onClick={() => openUpdateWeightsDialog('ZPMC')}>
                      Actualizar pesos ZPMC
                    </Button>
                    <Button variant="contained" color="primary" startIcon={<Autorenew />} onClick={() => openUpdateWeightsDialog('ZPEL')}>
                      Actualizar pesos ZPEL
                    </Button>
                  </>
                )}
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={openCreateDialog}>
                  {tabValue === 0 && 'Agregar variable'}
                  {tabValue === 1 && 'Agregar extra variable'}
                  {tabValue === 2 && 'Agregar pesos'}
                </Button>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TextField
                  size="small"
                  label="Buscar"
                  placeholder={
                    tabValue === 0
                      ? 'Tipo, categoría, peso inicial, peso final, paradas'
                      : tabValue === 1
                        ? 'Tipo, categoría, negocio, rango de medición'
                        : 'Fase, tipo, peso'
                  }
                  value={searchQuery}
                  onChange={(event) => setSearchQueries((current) => ({
                    ...current,
                    [tabValue]: event.target.value,
                  }))}
                  sx={{ width: { xs: '100%', sm: 420 } }}
                />
              </Grid>
            </Grid>

            {/* Config Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              <Table>
                <TableHead sx={{ bgcolor: '#f4f7f6' }}>
                  <TableRow>
                    {/* Column '#' removed as requested */}
                    {tabValue === 0 && (
                      <>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Peso inicial</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Peso final</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Paradas</TableCell>
                      </>
                    )}
                    {tabValue === 1 && (
                      <>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Negocio</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Rango de medición</TableCell>
                      </>
                    )}
                    {tabValue === 2 && (
                      <>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fase</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Peso</TableCell>
                      </>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <TableRow key={item.id || index} hover sx={{ '&:hover': { bgcolor: '#f9fbfd' } }}>
                        {tabValue === 0 && (
                          <>
                            <TableCell align="center">{item.phase || '-'}</TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 'regular' }}>{item.category || 'General'}</Typography>
                            </TableCell>
                            <TableCell align="center">{item.startWeight || item.start_weight || 0}</TableCell>
                            <TableCell align="center">{item.endWeight || item.end_weight || 0}</TableCell>
                            <TableCell align="center">{item.elevatorStop || item.elevator_stop || 0}</TableCell>
                          </>
                        )}
                        {tabValue === 1 && (
                          <>
                            <TableCell align="center">{item.phase || '-'}</TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 'regular' }}>{item.category || '-'}</Typography>
                            </TableCell>
                            <TableCell align="center">{item.business || '-'}</TableCell>
                            <TableCell align="center">{item.measuringRange || item.measuring_range || '-'}</TableCell>
                          </>
                        )}
                        {tabValue === 2 && (
                          <>
                            <TableCell align="center">{item.phase || '-'}</TableCell>
                            <TableCell align="center">{item.name || '-'}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                              {`${item.theoreticalQuantity || 0} %`}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {(tabValue === 0 || tabValue === 1 || tabValue === 2) ? (
                              <>
                                <Tooltip title="Editar registro">
                                  <IconButton color="primary" size="small" onClick={() => openEditDialog(item)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {(tabValue === 0 || tabValue === 1) && (
                                  <Tooltip title="Editar precio">
                                    <IconButton color="secondary" size="small" onClick={() => openPriceDialog(item)}>
                                      <Euro fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Eliminar">
                                  <IconButton color="error" size="small" onClick={() => handleDeleteRecord(item)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Editar">
                                  <IconButton color="primary" size="small">
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton color="error" size="small" onClick={() => handleDeleteRecord(item)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={tabValue === 0 ? 6 : (tabValue === 1 ? 5 : 4)} align="center" sx={{ py: 10 }}>
                        <Box sx={{ opacity: 0.5, textAlign: 'center' }}>
                          <Warning sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>No hay configuraciones encontradas</Typography>
                          <Typography variant="body2" color="text.secondary">Aún no se han registrado datos para esta categoría en el sistema.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <Dialog open={priceDialogOpen} onClose={closePriceDialog} fullWidth maxWidth="sm">
        <DialogTitle>Editar precio variable</DialogTitle>
        <DialogContent dividers>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha inicio</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Precio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const parseDate = (dateStr) => {
                  if (!dateStr) return 0;
                  const parts = dateStr.split(' ')[0].split('-');
                  if (parts.length < 3) return 0;
                  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getTime();
                };

                const sortedPrices = [...(selectedItem?.prices || [])].sort((a, b) => {
                  return parseDate(a.startDate) - parseDate(b.startDate);
                });

                return sortedPrices.length > 0 ? (
                  sortedPrices.map((price) => {
                    return (
                      <TableRow key={price.id}>
                        <TableCell>{formatDate(price.startDate)}</TableCell>
                        <TableCell>{formatPrice(price.price)}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">No hay precios registrados</TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha inicio"
                type="date"
                value={priceForm.startDate}
                onChange={(event) => setPriceForm((form) => ({ ...form, startDate: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Precio"
                type="number"
                value={priceForm.price}
                onChange={(event) => setPriceForm((form) => ({ ...form, price: event.target.value }))}
                inputProps={{ step: '0.01', min: 0 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePriceDialog}>Cerrar</Button>
          <Button variant="contained" onClick={handleCreatePrice} disabled={savingPrice}>
            Añadir registro
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isCreateMode ? (tabValue === 1 ? 'Nueva variable extra' : (tabValue === 2 ? 'Nuevo peso' : 'Nueva variable')) : 'Editar registro'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {(tabValue === 0 || tabValue === 1) && (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de ascensor"
                    value={editForm.phase}
                    onChange={(event) => setEditForm((form) => ({ ...form, phase: event.target.value }))}
                  >
                    {elevatorTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Categoría"
                    value={editForm.category}
                    onChange={(event) => setEditForm((form) => ({ ...form, category: event.target.value }))}
                  >
                    {(tabValue === 0 ? categoryOptions : extraCategoryOptions).map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {tabValue === 0 && (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Peso"
                    value={editForm.weightRange}
                    onChange={(event) => setEditForm((form) => ({ ...form, weightRange: event.target.value }))}
                  >
                    {weightRangeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Parada"
                    value={editForm.elevatorStop}
                    onChange={(event) => setEditForm((form) => ({ ...form, elevatorStop: event.target.value }))}
                  >
                    {stopOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {tabValue === 1 && (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Negocio"
                    value={editForm.business}
                    onChange={(event) => setEditForm((form) => ({ ...form, business: event.target.value }))}
                  >
                    {businessOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Rango de medida"
                    value={editForm.measuringRange}
                    onChange={(event) => setEditForm((form) => ({ ...form, measuringRange: event.target.value }))}
                  >
                    {measuringRangeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {tabValue === 2 && (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Fase"
                    value={editForm.phase}
                    onChange={(event) => setEditForm((form) => ({ ...form, phase: event.target.value }))}
                  >
                    {['ZPMC', 'ZPEL'].map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo"
                    value={editForm.name}
                    onChange={(event) => setEditForm((form) => ({ ...form, name: event.target.value }))}
                  >
                    {(editForm.phase === 'ZPEL'
                      ? ['Entrega a Cliente y PV', 'Montaje Armario Maniobra', 'Montaje de Cabina', 'Autocontrol', 'Ajustes, Regulación y Autocontrol', 'Instalación de Hueco']
                      : ['Reparto de material', 'Montaje de Guías', 'Autocontrol', 'Replanteo', 'Arranque de Guías', 'Puesta en Tiro', 'Montaje de Puertas']
                    ).map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Peso"
                    type="number"
                    value={editForm.theoreticalQuantity}
                    onChange={(event) => setEditForm((form) => ({ ...form, theoreticalQuantity: event.target.value }))}
                    inputProps={{ step: '1', min: 0, max: 100 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Fecha inicio"
                    type="date"
                    value={editForm.startDate}
                    onChange={(event) => setEditForm((form) => ({ ...form, startDate: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancelar</Button>
          {isCreateMode ? (
            <Button variant="contained" onClick={handleCreateRecord} disabled={savingRecord}>
              Aceptar
            </Button>
          ) : (
            <Button variant="contained" onClick={handleUpdateRecord} disabled={savingRecord}>
              Actualizar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={updateWeightsDialogOpen} onClose={closeUpdateWeightsDialog} fullWidth maxWidth="sm">
        <DialogTitle>Actualizar los pesos {updateWeightsPhase}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {(updateWeightsPhase === 'ZPEL' ? zpelFields : zpmcFields).map((field) => (
              <Grid item xs={12} key={field}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {field}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={updateWeightsForm[field] ?? ''}
                  onChange={(event) => setUpdateWeightsForm((form) => ({ ...form, [field]: event.target.value }))}
                  inputProps={{ step: '1', min: 0, max: 100 }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={handleUpdateWeights} disabled={savingWeights}>
            Actualizar
          </Button>
          <Button variant="outlined" onClick={closeUpdateWeightsDialog}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InstallationExtraListView;
